import type React from "react";
import { SetStateAction, useState } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";

import {
  isCollapsibleAtom,
  sidenavHoverSuppressedAtom,
  sidenavRevealAtom,
} from "./atoms";
import { useIsSidenavCollapsible } from "./useIsSidenavCollapsible";

export const useSidenavOpenState = () => {
  const isCollapsible = useIsSidenavCollapsible();
  const setIsCollapsibleAtom = useSetAtom(isCollapsibleAtom);
  const isRevealed = useAtomValue(sidenavRevealAtom);
  const setIsRevealed = useSetAtom(sidenavRevealAtom);
  const [isHoverSuppressed, setIsHoverSuppressed] = useAtom(
    sidenavHoverSuppressedAtom
  );
  const [isHovered, setIsHoveredState] = useState(false);
  // The menus open inside the sidenav, by name: it stays open while any is.
  const [blockers, setBlockers] = useState<ReadonlySet<string>>(new Set());
  const isCollapseBlocked = blockers.size > 0;
  const setIsCollapseBlocked = (menu: string, blocked: boolean) =>
    setBlockers((prev) => {
      if (prev.has(menu) === blocked) return prev;
      const next = new Set(prev);
      if (blocked) next.add(menu);
      else next.delete(menu);
      return next;
    });
  // A sidenav that replaces another (a page change) starts collapsed: the
  // reveal left by the pointer over the old one is cleared as it mounts, and
  // the next mouse move decides afresh.
  const [isMounted, setIsMounted] = useState(false);
  const attach = (el: HTMLElement | null) => {
    if (!el || isMounted) return;
    setIsRevealed(false);
    setIsMounted(true);
  };

  // Opens and closes at once: no delay either way.
  const open =
    !isCollapsible ||
    isCollapseBlocked ||
    (!isHoverSuppressed && ((isMounted && isRevealed) || isHovered));

  const setIsCollapsible = (value: SetStateAction<boolean>) => {
    const next = typeof value === "function" ? value(isCollapsible) : value;
    if (next && !isCollapsible) {
      setIsHoverSuppressed(true);
      setIsRevealed(false);
    }
    setIsCollapsibleAtom(next);
  };

  const setIsHovered = (value: boolean) => {
    setIsHoveredState(value);
    if (!value) setIsHoverSuppressed(false);
  };

  return {
    attach,
    open,
    isCollapsible,
    setIsCollapsible,
    setIsHovered,
    isCollapseBlocked,
    setIsCollapseBlocked,
  };
};

// What opens a collapsed sidebar: the pointer over it, or keyboard focus in it
// (so a keyboard user reaches what only shows when it is open). A click also
// focuses, but must not hold it open once the pointer has gone.
// A sidebar menu's content is portalled out of the sidebar; focus moving to or
// from it is the menu opening or closing, not the user arriving or leaving.
const isMenu = (element: EventTarget | null) =>
  element instanceof Element && !!element.closest('[role="menu"]');

export const revealHandlers = (setIsHovered: (value: boolean) => void) => ({
  onMouseEnter: () => setIsHovered(true),
  onMouseLeave: () => setIsHovered(false),
  onFocus: (event: React.FocusEvent<HTMLElement>) => {
    if (event.target.matches(":focus-visible") && !isMenu(event.relatedTarget))
      setIsHovered(true);
  },
  onBlur: (event: React.FocusEvent<HTMLElement>) => {
    if (
      !event.currentTarget.contains(event.relatedTarget) &&
      !isMenu(event.relatedTarget)
    )
      setIsHovered(false);
  },
});
